import { type FC, useEffect } from 'react'
import { useQuery } from '@based/react'
import { useAppCtx } from '../providers/useAppCtx'

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string
          language: string
          callback: (token: string) => void
          theme: 'dark' | 'light'
        },
      ) => void
      reset: () => void
    }
  }
}

const loadTurnstileScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true

    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Turnstile script'))

    document.head.appendChild(script)
  })
}

export const renderTurnstileWidget = async (
  containerId: string,
  siteKey: string,
  langCode: string,
  callback: (token: string) => void,
): Promise<void> => {
  await loadTurnstileScript()

  const container = document.getElementById(containerId)
  if (!container)
    throw new Error(`Container with ID "${containerId}" not found`)

  if (window.turnstile) {
    window.turnstile.render(container, {
      sitekey: siteKey,
      language: langCode,
      theme: 'light',
      callback,
    })
  } else {
    throw new Error('Turnstile global object is not available')
  }
}

export const resetTurnstileWidget = () => {
  if (window.turnstile) {
    window.turnstile.reset()
  }
}

export const Turnstile: FC<{ setTurnstileToken: (token: string) => void }> = ({
  setTurnstileToken,
}) => {
  const { activeLanguage } = useAppCtx()
  const {
    data: { turnstileSitekey },
  } = useQuery('payment-stripe-pubkey')

  useEffect(() => {
    renderTurnstileWidget(
      'turnstile-container',
      turnstileSitekey,
      activeLanguage,
      setTurnstileToken,
    ).catch((error) => console.error(error))
  }, [
    turnstileSitekey,
    /*lang.code*/
  ])

  return <div id="turnstile-container" />
}
